import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { track } from "@/lib/analytics";

const MAX_POLLS = 12;

export default function UpgradeSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = usePortalAuth();
  const [state, setState] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const status = await portalApi.subscriptionStatus(sessionId);
        if (status.payment_status === "paid") {
          await refresh();
          setState("paid");
          track("subscription_activated", { session_id: sessionId, plan: status.plan });
          return;
        }
        if (["expired", "failed"].includes(status.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <PortalLayout>
      <div className="max-w-xl py-10" data-testid="upgrade-success-page">
        {state === "checking" && (
          <div data-testid="upgrade-checking">
            <Loader2 className="animate-spin text-[#D4AF37] mb-6" size={30} />
            <h1 className="font-serif text-3xl text-white mb-3">Confirming your payment</h1>
            <p className="text-slate-400 text-sm">One moment — we are checking with Stripe.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="upgrade-paid">
            <CheckCircle2 className="text-[#D4AF37] mb-6" size={34} />
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">You're on Premium</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-9">
              Unlimited incident logging is on, and every vulnerability in your report now comes with its full set of
              counter-strategies.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/portal/report"
                data-testid="upgrade-to-report-btn"
                className="bg-[#D4AF37] text-slate-950 px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
              >
                Open my report
              </Link>
              <Link
                to="/portal"
                className="border border-slate-700 text-slate-300 px-7 py-3.5 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                Dashboard
              </Link>
            </div>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="upgrade-error">
            <XCircle className="text-red-400 mb-6" size={32} />
            <h1 className="font-serif text-3xl text-white mb-4">
              {state === "timeout" ? "Still processing" : "We couldn't confirm that payment"}
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-9">
              {state === "timeout"
                ? "Stripe is taking longer than usual. Your plan will update automatically once the payment settles."
                : "No charge has been applied if the checkout did not complete. You can try again at any time."}
            </p>
            <Link
              to="/portal/pricing"
              data-testid="upgrade-retry-btn"
              className="border border-[#D4AF37] text-[#D4AF37] px-7 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
            >
              Back to plans
            </Link>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
